import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { AttachFile } from '@mui/icons-material';
import BooleanQuestion from './BooleanQuestion';
import FilePreviewCard from './FilePreviewCard';

const ProjectDetailsStep = ({ form, errors, update, files, setFiles, selectFiles, productLines, maximumFileCount }) => {
  const { t } = useTranslation();

  return (
    <Stack spacing={4}>
      <FormControl error={errors.productLines} required>
        <FormLabel>{t('showroomProjectRequest.productLines')}</FormLabel>
        <FormGroup>
          {productLines.map(([field, label]) => (
            <FormControlLabel
              key={field}
              control={<Checkbox checked={form[field]} onChange={event => update(field, event.target.checked)} />}
              label={label}
            />
          ))}
        </FormGroup>
        {errors.productLines && <FormHelperText>{t('showroomProjectRequest.productRequired')}</FormHelperText>}
      </FormControl>
      <TextField
        required
        type="number"
        inputProps={{ min: 1 }}
        label={t('showroomProjectRequest.squareFootage')}
        value={form.totalBrpZoneSquareFootage}
        onChange={event => update('totalBrpZoneSquareFootage', event.target.value)}
        error={errors.totalBrpZoneSquareFootage}
        helperText={errors.totalBrpZoneSquareFootage && t('showroomProjectRequest.positiveNumber')}
        fullWidth
      />
      <BooleanQuestion
        label={t('showroomProjectRequest.hasBlueprints')}
        value={form.hasCurrentBlueprintsAndPictures}
        onChange={value => update('hasCurrentBlueprintsAndPictures', value)}
        error={errors.hasCurrentBlueprintsAndPictures}
        noLabel={
          <Box sx={{ mt: 2 }}>
            <Typography component="span">{t('showroomProjectRequest.no')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('showroomProjectRequest.siteSurveyNotice')}
            </Typography>
            {t('locale') === 'en' && (
              <Link href={`/files/BRP%20-%20Site%20Survey%20Package.pdf`} download>
                {t('showroomProjectRequest.downloadSiteSurveyPackage')}
              </Link>
            )}
          </Box>
        }
      />
      <Box>
        <Typography variant="h5" sx={{ mb: 1 }}>
          {t('showroomProjectRequest.upload')}
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          {t('showroomProjectRequest.uploadHelp')}
        </Typography>
        <Button component="label" variant="outlined" startIcon={<AttachFile />} disabled={files.length >= maximumFileCount}>
          {t('showroomProjectRequest.addFiles')}
          <input hidden multiple type="file" onChange={selectFiles} />
        </Button>
        {errors.files && <FormHelperText error>{t('showroomProjectRequest.invalidFiles')}</FormHelperText>}
        <Box
          sx={{
            mt: 2,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' },
            gap: 2,
          }}>
          {files.map((file, index) => (
            <FilePreviewCard
              key={`${file.name}-${index}`}
              file={file}
              onDelete={() => setFiles(current => current.filter((_, fileIndex) => fileIndex !== index))}
              deleteLabel={t('showroomProjectRequest.delete')}
            />
          ))}
        </Box>
      </Box>
    </Stack>
  );
};

export default ProjectDetailsStep;
